import { GetStaticProps, NextPage } from "next";
import { PageProps } from "../../types/pageProps";
import { Previewable } from "../../types/previewable";
import { getStaticPageData } from "../../utils/getStaticPageData";
import { revalidationInterval } from "../../utils/revalidationInterval";

import "pure-react-carousel/dist/react-carousel.es.css";
import {
  PricingLandingData,
  getPricingLandingQuery,
} from "../../queries/pricingLandingQuery";
import Block from "../../components/block";
import { useEffect, useMemo, useState } from "react";
import { State } from "../../types/state";
import { debounce } from "../../utils/debounce";
import {
  PricingResponse,
  ProductPricingResponse,
} from "../../types/coreApiMirror";
import { AdoptionTaxCredit } from "../../components/adoptionTaxCredit/adoptionTaxCredit";
import Questions from "../../components/questions";
import { ProductsComparison } from "../../components/pricingComparisonTable/productsComparisonModule";
import { DonatedFund } from "../../components/adoptionTaxCredit/donatedFund";
import { Benefits } from "../../components/adoptionTaxCredit/benefits";
import { useCoreAPI } from "../../hooks/useCoreAPI";
import { QueryParams } from "next-sanity";
import LiveQuery from "@sanity/preview-kit/live-query";
import PricingPlans from "@/components/PricingPlans/PricingPlans";
import SlidingTestimonialsCarousel from "@/components/SlidingTestimonialsCarousel/SlidingTestimonialsCarousel";
import HeaderNavigation from "@/components/HeaderNavigation/HeaderNavigation";
import {
  HeaderNavigationProps,
  HeaderNavigationQuery,
} from "@/components/HeaderNavigation";
import { getClient } from "lib/sanity.server";
import { isEmpty } from "@/utils/isEmpty";
import { AdoptionPlansHero } from "@/components/AdoptionPlansHero";
import SEOPageHead from "@/components/SEOPageHead";
import MultiColumnSection from "@/components/MultiColumnSection/MultiColumnSection";
import {
  FooterNavigationProps,
  FooterNavigationQuery,
} from "@/components/FooterNavigation/query";
import FooterModular from "@/components/FooterModular";

export type PricingLandingProps = Previewable<PageProps<PricingLandingData>>;

const token = process.env.SANITY_API_TOKEN;

const PricingLanding: NextPage<PricingLandingProps> = (
  props: PricingLandingProps,
) => {
  const pricingLandingData = props.payload.data;

  const CoreAPI = useCoreAPI();
  const [selectedState, setSelectedState] = useState<State>();
  const [currentPrices, setCurrentPrices] = useState<ProductPricingResponse[]>(
    [],
  );

  const runSearch = useMemo(
    () =>
      debounce(async (selectedState: State | undefined) => {
        const pricesResponse = await CoreAPI.getPrices(selectedState?.code);
        if (pricesResponse.ok) {
          const pricesWithInternalId = [];
          for (let key of Object.keys(pricesResponse.content)) {
            //transforming the object key in a field inside the object to facilitate the search through an array of producsts instead of object of objects of type product
            pricesWithInternalId.push(
              ...pricesResponse.content[key as keyof PricingResponse].map(
                (item) => {
                  return { ...item, internalId: key };
                },
              ),
            );
          }
          setCurrentPrices(pricesWithInternalId);
        }
      }),
    [selectedState],
  );

  if (pricingLandingData === null) {
    return null;
  }
  const {
    states,
    image,
    moduleAdoptionTaxCredit,
    moduleQuestions,
    moduleProductsComparison,
    pricing,
    testimonials,
    navSections,
    hero,
    colorway,
    seo,
    providers,
    footerPayload,
  }: PricingLandingData = pricingLandingData;

  return (
    <>
      <LiveQuery
        enabled={props.preview}
        query={getPricingLandingQuery(props.preview)}
        initialData={props.payload.data}
      >
        <>
          {seo && <SEOPageHead seo={seo} />}
          <HeaderNavigation navSections={navSections} />

          <AdoptionPlansHero hero={hero} colorway={colorway} />

          <Block>
            <PricingPlans {...pricing} />
          </Block>

          {!isEmpty(providers) &&
            providers.map((provider, i) => (
              <MultiColumnSection key={i} colorway={colorway} {...provider} />
            ))}

          <SlidingTestimonialsCarousel {...testimonials} colorway={colorway} />

          <>
            {moduleAdoptionTaxCredit &&
              moduleAdoptionTaxCredit.moduleFeatureFlag?.enabled && (
                <Block>
                  <div className="grid w-full grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
                    <DonatedFund
                      moduleInfo={moduleAdoptionTaxCredit.moduleInput}
                    />
                    <Benefits
                      moduleInfo={moduleAdoptionTaxCredit.moduleInput}
                    />
                    <AdoptionTaxCredit
                      moduleInfo={moduleAdoptionTaxCredit.moduleInput}
                    />
                  </div>
                </Block>
              )}

            {moduleProductsComparison &&
              moduleProductsComparison.moduleFeatureFlag?.enabled &&
              states &&
              image && (
                <Block>
                  <ProductsComparison
                    moduleInfo={{ moduleProductsComparison }}
                    states={states}
                    stateSelectorImage={image}
                    updateState={setSelectedState}
                    selectedState={selectedState}
                    prices={currentPrices}
                  />
                </Block>
              )}

            {moduleQuestions && moduleQuestions.moduleFeatureFlag?.enabled && (
              <>
                <Block color="darkblue" flushBottom>
                  <Questions moduleInfo={{ moduleQuestions }} />
                </Block>
              </>
            )}
          </>
          <FooterModular footerData={props.payload.data.footerPayload} />
        </>
      </LiveQuery>
    </>
  );
};

export const getStaticProps: GetStaticProps<PricingLandingProps> = async (
  context,
) => {
  const preview: boolean = !!context.draftMode;
  const queryParams: QueryParams = {};
  const query = getPricingLandingQuery(preview);
  const pricingPayload = await getStaticPageData<PricingLandingData>({
    query,
    queryParams,
    preview,
  });

  if (pricingPayload === null) {
    return {
      notFound: true,
    };
  }

  const navPayload: HeaderNavigationProps = await getClient(preview).fetch(
    HeaderNavigationQuery.query,
    {},
  );

  const footerPayload: FooterNavigationProps = await getClient(preview).fetch(
    FooterNavigationQuery.query,
    {},
  );

  if (isEmpty(navPayload)) {
    return {
      notFound: true,
    };
  }

  const payloadWithNav = {
    ...pricingPayload,
    data: {
      ...pricingPayload.data,
      ...navPayload,
      footerPayload: footerPayload,
    },
  };

  return {
    props: {
      payload: { ...payloadWithNav },
      preview,
      token: preview ? token : "",
    },
    revalidate: revalidationInterval,
  };
};

export default PricingLanding;
